export function delay(ms: number): Promise<void> {
    return new Promise(resolve => {
        setTimeout(resolve, ms)
    })
}

abstract class RetryStrategy {
    constructor(protected maxRuns?: number) {}

    public shouldRetry(runNumber: number): boolean {
        return typeof this.maxRuns === 'undefined' || runNumber < this.maxRuns
    }

    public abstract sleep(runNumber: number): Promise<void>
}

export class ConstantBackoffRetryStrategy extends RetryStrategy {
    constructor(private startDelay: number, maxRuns?: number) {
        super(maxRuns)
    }

    public override async sleep(): Promise<void> {
        await delay(this.startDelay)
    }
}

export class ExponentialBackoffRetryStrategy extends RetryStrategy {
    constructor(
        private startDelay: number,
        maxRuns?: number,
        private maxDelay?: number,
        private exponentBase = 2,
        private maxTotalDelay?: number
    ) {
        super(maxRuns)
    }

    public override shouldRetry(runNumber: number): boolean {
        if (typeof this.maxTotalDelay !== 'undefined') {
            const totalDelay = Array(runNumber)
                .fill(0)
                .map((_, x) => this.delayTime(x))
                .reduce((acc, x) => acc + x)
            if (totalDelay >= this.maxTotalDelay) {
                return false
            }
        }
        return typeof this.maxRuns === 'undefined' || runNumber < this.maxRuns
    }

    private delayTime(runNumber: number): number {
        const sleepTime = this.startDelay * Math.pow(this.exponentBase, runNumber)
        return Math.min(sleepTime, this.maxDelay ?? Infinity)
    }

    public override async sleep(runNumber: number): Promise<void> {
        await delay(this.delayTime(runNumber))
    }
}

export async function runWhile(
    exec: () => Promise<boolean> | boolean,
    retryStrategy: RetryStrategy,
    timeoutErrorMessage?: string
): Promise<void>
export async function runWhile(
    exec: () => Promise<boolean> | boolean,
    periodInMs: number,
    maxRuns?: number,
    timeoutErrorMessage?: string
): Promise<void>
export async function runWhile(
    exec: () => Promise<boolean> | boolean,
    periodInMsOrRetryStrategy: number | RetryStrategy,
    maxRunsOrTimeoutErrorMessage?: number | string,
    timeoutErrorMessageOrNothing?: string
): Promise<void> {
    const retryStrategy =
        typeof periodInMsOrRetryStrategy === 'number'
            ? new ConstantBackoffRetryStrategy(
                  periodInMsOrRetryStrategy,
                  typeof maxRunsOrTimeoutErrorMessage === 'number' ? maxRunsOrTimeoutErrorMessage : undefined
              )
            : periodInMsOrRetryStrategy

    const timeoutErrorMessage =
        (typeof maxRunsOrTimeoutErrorMessage === 'string'
            ? maxRunsOrTimeoutErrorMessage
            : timeoutErrorMessageOrNothing) ?? 'Operation timed out'

    let runNumber = 0
    while (true) {
        runNumber++
        const shouldContinue = await exec()
        if (!shouldContinue) return

        if (retryStrategy.shouldRetry(runNumber)) {
            await retryStrategy.sleep(runNumber)
        } else {
            throw new Error(timeoutErrorMessage)
        }
    }
}

/**
 * Runs `exec` function until it returns something non-null.
 */
export async function runUntil<T extends NonNullable<unknown>>(
    exec: () => Promise<T | null>,
    retryStrategy: RetryStrategy,
    timeoutErrorMessage?: string
): Promise<T>
/**
 * Runs `exec` function until it returns something non-null.
 */
export async function runUntil<T extends NonNullable<unknown>>(
    exec: () => Promise<T | null>,
    periodInMs: number,
    maxRuns?: number,
    timeoutErrorMessage?: string
): Promise<T>
export async function runUntil<T extends NonNullable<unknown>>(
    exec: () => Promise<T | null>,
    periodInMsOrRetryStrategy: number | RetryStrategy,
    maxRunsOrTimeoutErrorMessage?: number | string,
    timeoutErrorMessageOrNothing?: string
): Promise<T> {
    const retryStrategy =
        typeof periodInMsOrRetryStrategy === 'number'
            ? new ConstantBackoffRetryStrategy(
                  periodInMsOrRetryStrategy,
                  typeof maxRunsOrTimeoutErrorMessage === 'number' ? maxRunsOrTimeoutErrorMessage : undefined
              )
            : periodInMsOrRetryStrategy

    const timeoutErrorMessage =
        (typeof maxRunsOrTimeoutErrorMessage === 'string'
            ? maxRunsOrTimeoutErrorMessage
            : timeoutErrorMessageOrNothing) ?? 'Operation timed out'

    let runNumber = 0
    while (true) {
        runNumber++
        const result = await exec()
        if (result !== null) return result

        if (retryStrategy.shouldRetry(runNumber)) {
            await retryStrategy.sleep(runNumber)
        } else {
            throw new Error(timeoutErrorMessage)
        }
    }
}
